import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Space,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';

import { CustomAlert } from '@app/components';
import {
  DATE_PICKER_FORMAT,
  ERROR,
  ISO_FORMAT_INDEX,
  PREFIX,
  rangePickerLocale,
  WARNING,
} from '@app/constants';
import type { NEmployees } from '@app/types/Employees';

import { useEmployeeAdditionalInfoTab } from '@pages/Employees/EditEmployee/components/EmployeeAdditionalInfoTab/hooks/useEmployeeAdditionalInfoTab';
import { useRegisterEditEmployeeUnsavedChanges } from '@pages/Employees/EditEmployee/hooks/useEditEmployeeUnsavedChanges';

import '@pages/Employees/EditEmployee/components/EmployeeAdditionalInfoTab/styles/EmployeeAdditionalInfoTab.desktop.scss';
import '@pages/Employees/EditEmployee/components/EmployeeAdditionalInfoTab/styles/EmployeeAdditionalInfoTab.tablet.scss';

const { Text } = Typography;
const { TextArea } = Input;

export const EmployeeAdditionalInfoTab = (
  data: NEmployees.EmployeeAdditionalTabProps
) => {
  const {
    employee,
    setNewData,
    newData,
    setEmployee,
    setIsNeedToUpdateUserPreview,
    setHandleUnsavedChanges,
  } = data;

  const {
    form,
    submitForm,
    isLoading,
    error,
    setError,
    isUpdated,
    setIsUpdated,
    handleUnsavedChanges,
  } = useEmployeeAdditionalInfoTab(
    employee,
    setEmployee,
    setIsNeedToUpdateUserPreview
  );

  useEffect(() => {
    form.setFieldsValue({
      dateWorkStart: employee?.dateWorkStart
        ? dayjs(employee?.dateWorkStart.slice(0, 10))
        : '',
      datePatentEnd: employee?.datePatentEnd
        ? dayjs(employee?.datePatentEnd.slice(0, 10))
        : '',
      citizenship: employee?.citizenship && employee?.citizenship,
      passportInfo: employee?.passportInfo && employee?.passportInfo,
      taxPayerId: employee?.taxPayerId && employee?.taxPayerId,
      insuranceCertificateN:
        employee?.insuranceCertificateN && employee?.insuranceCertificateN,
    });
    setIsUpdated(false);
  }, [form, employee, setIsUpdated]);

  useRegisterEditEmployeeUnsavedChanges({
    handleUnsavedChanges,
    setHandleUnsavedChanges,
  });

  return (
    <div className={`${PREFIX}additional-info`}>
      <Form
        style={{ maxWidth: '800px', width: '100%' }}
        onFinish={(values) => submitForm(values, employee)}
        onValuesChange={() => setIsUpdated(true)}
        form={form}
      >
        <Space direction="vertical" size="large" className="form-space">
          <div className="container">
            <Text className="label" strong data-testid="date-of-employment">
              Дата приёма на работу
            </Text>

            <Form.Item name="dateWorkStart" className="mg-0">
              <DatePicker
                data-testid="date-of-employment-input"
                locale={rangePickerLocale}
                format={DATE_PICKER_FORMAT}
                size="large"
                onChange={(value) => {
                  const newValue = {
                    ...newData.additionalInfoData,
                    dateWorkStart: value
                      ? value.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
                      : null,
                  };

                  setNewData({
                    ...newData,
                    additionalInfoData: newValue,
                  });
                }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Text className="label" strong data-testid="date-of-registration">
              Дата окончания регистрации/патента
            </Text>

            <Form.Item name="datePatentEnd" className="mg-0">
              <DatePicker
                data-testid="date-of-registration-input"
                locale={rangePickerLocale}
                format={DATE_PICKER_FORMAT}
                size="large"
                onChange={(value) => {
                  const newValue = {
                    ...newData.additionalInfoData,
                    datePatentEnd: value
                      ? value.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
                      : null,
                  };

                  setNewData({
                    ...newData,
                    additionalInfoData: newValue,
                  });
                }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Text className="label" strong data-testid="nationality">
              Гражданство
            </Text>

            <Form.Item name="citizenship" className="mg-0">
              <Input
                data-testid="nationality-input"
                size="large"
                maxLength={30}
                onChange={(e) => {
                  const newValue = {
                    ...newData.additionalInfoData,
                    citizenship: e.target.value,
                  };
                  setNewData({
                    ...newData,
                    additionalInfoData: newValue,
                  });
                }}
              />
            </Form.Item>
          </div>

          <div className="container text-area">
            <Typography.Text
              className="label"
              strong
              data-testid="passport-data"
            >
              Паспортные данные
            </Typography.Text>

            <Form.Item name="passportInfo" className="mg-0">
              <TextArea
                data-testid="passport-data-input"
                rows={4}
                maxLength={400}
                onChange={(e) => {
                  const newValue = {
                    ...newData.additionalInfoData,
                    passportInfo: e.target.value,
                  };
                  setNewData({
                    ...newData,
                    additionalInfoData: newValue,
                  });
                }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Text className="label" strong data-testid="tin-title">
              ИНН
            </Text>

            <Form.Item name="taxPayerId" className="mg-0">
              <InputNumber
                data-testid="tin-input"
                size="large"
                controls={false}
                onChange={(value) => {
                  const newValue = {
                    ...newData.additionalInfoData,
                    taxPayerId: value ? Number(value) : value === 0 ? 0 : null,
                  };
                  setNewData({
                    ...newData,
                    additionalInfoData: newValue,
                  });
                }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Text
              className="label"
              strong
              data-testid="number-of-insurance-certificate"
            >
              Номер страхового свидетельства
            </Text>

            <Form.Item name="insuranceCertificateN" className="mg-0">
              <Input
                data-testid="insurance-certificate-number-input"
                size="large"
                maxLength={20}
                onChange={(e) => {
                  const newValue = {
                    ...newData.additionalInfoData,
                    insuranceCertificateN: e.target.value,
                  };
                  setNewData({
                    ...newData,
                    additionalInfoData: newValue,
                  });
                }}
              />
            </Form.Item>
          </div>

          {error && (
            <CustomAlert
              errorType={ERROR}
              serverError={error}
              setServerError={setError}
            />
          )}

          {isUpdated && (
            <CustomAlert
              errorType={WARNING}
              serverError={'Есть несохранённые изменения'}
            />
          )}

          <Button
            type="primary"
            htmlType="submit"
            className="save-button"
            size="large"
            loading={isLoading}
            data-testid="save"
          >
            Сохранить
          </Button>
        </Space>
      </Form>
    </div>
  );
};
